import { BarChart3, Plus, Trash2 } from "lucide-react";
import { DiscordChannel, DiscordRole, GuildConfig } from "@/lib/api";
import { Section, Field } from "@/components/panel/Section";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { ChannelPicker } from "@/components/panel/ChannelPicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type StatChannelsConfig = GuildConfig["stat_channels"];
type StatChannel = StatChannelsConfig["channels"][number];

type Props = {
  config: GuildConfig;
  channels: DiscordChannel[];
  roles: DiscordRole[];
  onChange: (v: StatChannelsConfig) => void;
};

const emptyStatChannel = (): StatChannel => ({
  id: `stat_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  enabled: true,
  channel_id: "",
  stat_type: "members",
  name_template: "Members: {count}",
  role_id: "",
});

export function StatChannelsTab({ config, channels, roles, onChange }: Props) {
  const cfg = config.stat_channels ?? { enabled: false, update_minutes: 15, channels: [] };
  const textAndVoice = channels.filter((ch) => ch.type === 0 || ch.type === 2);

  const update = <K extends keyof StatChannelsConfig>(key: K, value: StatChannelsConfig[K]) => {
    onChange({ ...cfg, [key]: value });
  };

  const updateItem = (idx: number, patch: Partial<StatChannel>) => {
    const next = [...(cfg.channels || [])];
    next[idx] = { ...next[idx], ...patch };
    update("channels", next);
  };

  return (
    <Section
      icon={BarChart3}
      title="Stat Channels"
      description="Automatically rename channels with live server stats like members, humans, bots, boosts or a role count."
      enabled={cfg.enabled}
      onToggle={(v) => update("enabled", v)}
      actions={
        <Button size="sm" onClick={() => update("channels", [...(cfg.channels || []), emptyStatChannel()])}>
          <Plus className="h-4 w-4" />
          Add stat
        </Button>
      }
    >
      <div className="rounded-lg border border-dashed border-border bg-muted/25 p-4 text-sm text-muted-foreground space-y-2">
        <p className="font-medium text-foreground">How it works</p>
        <p>
          <strong>No slash commands.</strong> On each interval the bot updates channel names using your templates (e.g.{" "}
          <code className="text-primary">{"Members: {count}"}</code>). Respect Discord rename rate limits—the minimum
          interval is enforced below.
        </p>
      </div>

      <Field label="Update every X minutes" hint="Discord rate limits channel renames, so 5 minutes is the minimum.">
        <Input
          type="number"
          min={5}
          max={1440}
          value={cfg.update_minutes}
          onChange={(e) => update("update_minutes", Math.max(5, Number(e.target.value) || 15))}
        />
      </Field>

      <div className="space-y-3">
        {(cfg.channels || []).map((item, idx) => (
          <div key={item.id || idx} className="rounded-lg border border-border bg-muted/20 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Switch checked={item.enabled} onCheckedChange={(v) => updateItem(idx, { enabled: v })} />
              <Input
                value={item.name_template}
                onChange={(e) => updateItem(idx, { name_template: e.target.value })}
                placeholder="Members: {count}"
              />
              <Button
                size="icon"
                variant="ghost"
                onClick={() => update("channels", (cfg.channels || []).filter((_, i) => i !== idx))}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>

            <div className="grid md:grid-cols-3 gap-3">
              <Field label="Channel">
                <ChannelPicker
                  channels={textAndVoice}
                  value={item.channel_id}
                  onChange={(v) => updateItem(idx, { channel_id: v === "__none__" ? "" : v })}
                />
              </Field>
              <Field label="Stat type">
                <Select
                  value={item.stat_type}
                  onValueChange={(v) => updateItem(idx, { stat_type: v as StatChannel["stat_type"] })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="members">All members</SelectItem>
                    <SelectItem value="humans">Humans only</SelectItem>
                    <SelectItem value="bots">Bots only</SelectItem>
                    <SelectItem value="boosts">Boosts</SelectItem>
                    <SelectItem value="roles">Specific role</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Role for role count" hint="Only used when Stat type is Specific role.">
                <Select
                  value={item.role_id || "__none__"}
                  onValueChange={(v) => updateItem(idx, { role_id: v === "__none__" ? "" : v })}
                >
                  <SelectTrigger><SelectValue placeholder="Select role" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No role</SelectItem>
                    {roles.map((role) => (
                      <SelectItem key={role.id} value={role.id}>
                        @{role.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <p className="text-xs text-muted-foreground">
              Template variables: {"{label}"} and {"{count}"}. Example: Members: {"{count}"}
            </p>
          </div>
        ))}
      </div>
    </Section>
  );
}
